// English translations - Complete
export const en = {
  // Common
  common: {
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    search: 'Search',
    select: 'Select',
    or: 'Or',
    and: 'and',
    note: 'Note',
    viewAll: 'View All',
    learnMore: 'Learn More',
    getStarted: 'Get Started',
    continue: 'Continue',
    saving: 'Saving…',
  },

  // Offline / network resilience surfaces
  offline: {
    banner: "You're offline. Some features may be limited.",
    savedLocally: 'Saved offline — syncing when online',
    syncing: 'Syncing your updates…',
    backOnline: 'Back online — updates synced',
  },

  // Navigation
  nav: {
    dashboard: 'Home',
    nutrition: 'Nutrition',
    family: 'Family',
    care: 'Services',
    careShort: 'Services',
    checkIn: 'Check-In',
    insights: 'Insights',
    trends: 'Trends',
    habits: 'Habits',
    medications: 'Medications',
    expertSupport: 'Expert Support',
    profile: 'Profile',
    healthHub: 'Health Hub',
    support: 'Support',
    wellness: 'Wellness',
    education: 'Education',
    community: 'Community',
    preventiveHealth: 'Preventive Health',
    womensHealth: "Women's Health",
    reminders: 'Health Reminders',
    meals: 'Meals',
    orders: 'Orders',
    subscription: 'Subscription',
    logout: 'Logout',
  },

  // Short bottom-nav labels (≤9 chars). Falls back to nav.* when missing.
  navShort: {
    dashboard: 'Home',
    nutrition: 'Nutrition',
    family: 'Family',
    care: 'Services',
    support: 'Support',
    profile: 'Profile',
    stepTracker: 'Step Tracker',
  },

  // Auth
  auth: {
    login: 'Sign In',
    register: 'Create Account',
    logout: 'Sign Out',
    email: 'Email',
    password: 'Password',
    name: 'Full Name',
    forgotPassword: 'Forgot Password?',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',
    signInHere: 'Sign in here',
    signUpHere: 'Sign up here',
    welcomeBack: 'Welcome back!',
    getStarted: "Let's get started",
    biometricLogin: 'Sign in with Biometrics',
    biometricHelper: 'Use fingerprint, Face ID, or Windows Hello',
    vendorLogin: 'Vendor Login',
    createOne: 'Create one',
    tagline: 'Stay ahead of your health — one simple check each day.',
    takes30s: 'Takes less than 30 seconds',
    emailPlaceholder: 'you@example.com',
    passwordPlaceholder: 'Enter your password',
    checking: 'Checking…',
    continue: 'Continue',
    secureMessage: 'Secure and private — your data stays protected',
    biometricSoon: 'Biometric login — Coming Soon',
    sessionExpired: 'Your session expired. Please sign in again to pick up where you left off.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    continueWithGoogle: 'Continue with Google',
    orDivider: 'or',
    googleSigningIn: 'Signing you in…',
    googleFailed: 'Google sign-in failed. Please try again.',
    // Vendor login
    vendor: {
      title: 'Vendor Partner Login',
      subtitle: 'Access your HealthLoop vendor dashboard',
      businessEmail: 'Business Email',
      emailPlaceholder: 'vendor@business.com',
      passwordPlaceholder: '••••••••',
      signingIn: 'Signing in...',
      signIn: 'Sign In',
      notVendorYet: 'Not a vendor partner yet?',
      registerBusiness: 'Register your business',
      areYouUser: 'Are you a user?',
      userLogin: 'User Login',
      welcomeBackToast: 'Welcome back!',
      genericError: 'Login failed. Please try again.',
    },
    // Admin login
    admin: {
      backToUserLogin: 'User sign-in',
      title: 'Admin Sign-in',
      restrictedBadge: 'Restricted',
      tagline: 'HealthLoop operator console — admin credentials required.',
      emailLabel: 'Admin email',
      emailPlaceholder: 'admin@yourdomain.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your admin password',
      signingIn: 'Signing in…',
      submit: 'Sign in to admin console',
      secureNote: 'Secure connection · session encrypted',
      restrictedTitle: 'This area is restricted to admins.',
      restrictedBody: 'Your account is signed in, but it does not have admin access. Sign out to try a different account, or head back to the main app.',
      signOut: 'Sign out',
      backToApp: 'Back to app',
    },
  },

  // Quick Health Check (legacy quick check-in)
  quickCheck: {
    header: {
      title: 'How are you feeling today?',
      subtitle: 'Quick check — this takes less than 60 seconds',
    },
    section1: {
      title: 'What are you noticing today?',
      hint: '(Select all that apply)',
    },
    environment: {
      title: 'Your environment today',
      hint: 'Help us understand your surroundings',
    },
    basicChecks: {
      title: 'Quick basic checks',
      hint: 'Optional but helpful',
    },
    validation: {
      pickOne: 'Please answer at least one question',
    },
    submit: {
      cta: 'Get My Health Insight',
      saving: 'Saving...',
      analyzing: 'Analyzing your health patterns...',
      savedToast: '✅ Check-in saved! Analyzing your health patterns...',
    },
  },

  // Step Tracker (Enhanced)
  stepTracker: {
    header: {
      title: 'Step Tracker',
      subtitle: 'Honest, local-first activity tracking',
      backToDashboard: 'Back to dashboard',
    },
    today: {
      title: "Today's steps",
      goalProgress: '{{current}} / {{goal}} steps',
      goalReached: 'Goal reached — nice work!',
      keepGoing: "Keep going — you're on track.",
    },
    streak: {
      label: 'Current streak',
      days: '{{count}} day',
      daysPlural: '{{count}} days',
    },
    badges: {
      title: 'Badges',
      empty: "You'll earn badges as you build the habit.",
    },
    reminders: {
      title: 'Movement nudges',
      hint: 'Gentle reminders to take a short walk during long sits.',
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    insights: {
      weeklyAverage: 'Weekly average',
      weeklyGoal: 'Weekly goal',
    },
    actions: {
      connect: 'Connect a step source',
      manualLog: 'Log steps manually',
      changeGoal: 'Change daily goal',
    },
    offline: {
      title: 'Offline mode active',
      body: "Tracking continues even while offline. We'll sync your updates when you reconnect.",
    },
    permission: {
      title: 'Connect for automatic step tracking',
      body: 'Allow HealthLoop to read your {{platform}} data. Your wellness data stays on your device unless you choose to sync it.',
      connect: 'Connect',
      maybeLater: 'Maybe later',
    },
    sync: {
      lastSynced: 'Last synced {{time}}',
      queued: '{{count}} update queued to sync.',
      queuedPlural: '{{count}} updates queued to sync.',
      disconnect: 'Disconnect',
      refreshAria: 'Refresh sync',
    },
    progress: {
      title: "Today's Progress",
      stepsToday: 'steps today',
      goalLabel: 'Goal: {{goal}}',
      stepsToGo: '{{remaining}} steps to go',
      goalAchieved: '🎉 Goal achieved',
      goalAriaLabel: 'Goal achieved',
      statGoal: 'Goal',
      statProgress: 'Progress',
      statSource: 'Source',
      sourceDevice: 'Device',
      sourceManual: 'Manual',
    },
    empty: {
      waiting: 'Waiting for activity data',
      startMoving: 'Start moving to begin tracking',
      shortWalkHint: 'Take a short walk to test tracking — or log a manual entry below.',
    },
    manual: {
      title: 'Log Steps Manually',
      placeholder: 'Enter steps',
      submit: 'Log',
      footer: "Manual entries replace today's count. They sync automatically when you're online.",
    },
    trust: {
      title: 'Private by default',
      itemData: 'Your wellness data stays on your device unless synced to your account.',
      itemOffline: 'Tracking continues even while offline — updates queue and sync when you reconnect.',
      itemNoInvent: 'We never invent step counts. Only values from your device or your manual entries are stored.',
    },
    howItWorks: {
      title: 'How it works',
      itemConnect: "Connect to your device's health app for hands-free tracking.",
      itemManual: 'Manual entry is always available as a reliable fallback.',
      itemOfflineQueue: "Offline updates queue locally and sync the moment you're back online.",
      itemDisconnect: 'Disconnect anytime — your logged history stays on this device.',
    },
  },

  // Smart Daily Check-In flow
  checkin: {
    header: {
      title: 'Daily Health Check-In',
      subtitle: 'Takes less than 60 seconds',
      restDayLink: "I'm not feeling it today",
    },
    snapshot: {
      title: 'Your dashboard, updated',
      healthScore: 'Health score',
      todayTasks: "Today's tasks",
      dayStreak: 'day streak',
    },
    resume: {
      banner: 'Picked up where you left off',
      startFresh: 'Start fresh',
      dismiss: 'Dismiss',
    },
    progress: {
      allSet: 'All set',
      step: 'Step {{current}} of {{total}}',
      ariaLabel: 'Daily check-in progress: {{done}} of {{total}} sections complete',
    },
    insights: {
      title: 'Your Health Insights',
      subtitle: 'Personalised for you today',
      insightForYou: 'Insight for you',
      simpleActions: 'Simple actions you can take',
      whenToSeekHelp: 'When to seek help',
      nhsAdvice: 'If concerned, contact NHS 111 or your GP',
      backToDashboard: 'Back to Home',
      newCheckIn: 'New Check-In',
      disclaimer: 'HealthLoop provides guidance, not medical diagnosis.',
    },
    restDay: {
      title: 'Rest is part of the rhythm.',
      body: "We'll be here tomorrow, when you are.",
      streakNote: "Your streak hasn't moved — it's just resting too.",
      backToDashboard: 'Back to dashboard',
      changedMind: "Actually, I'd like to check in anyway.",
    },
    feelings: {
      title: 'How are you feeling today?',
      hint: 'Select all that apply',
      tired: 'Feeling tired',
      headache: 'Headache',
      stressed: 'Stressed or overwhelmed',
      pain: 'Body aches or pain',
      okay: 'I feel okay today',
    },
    basics: {
      title: 'Your basics',
      sleepQ: 'Did you sleep well last night?',
      waterQ: 'Have you had enough water today?',
      foodQ: 'Have you eaten properly today?',
      activityQ: 'How active were you today?',
      yes: 'Yes',
      notReally: 'Not really',
      notYet: 'Not yet',
      notActive: 'Not active',
      lightActivity: 'Light activity',
      active: 'Active',
      // Hydration enrichment (2026-05-24): replaces the binary water yes/no
      // with a felt 4-level scale + optional intake range. Educational, not
      // diagnostic. Other locales fall back to these EN strings until
      // native-speaker QA translates them post-launch.
      hydrationQ: 'How well hydrated were you today?',
      hydration_very_hydrated: 'Very hydrated',
      hydration_moderately: 'Moderately hydrated',
      hydration_slightly_dehydrated: 'Slightly dehydrated',
      hydration_very_dehydrated: 'Very dehydrated',
      waterIntakeQ: 'About how much water did you drink?',
      optional: 'optional',
      waterIntake_lt1: 'Less than 1L',
      waterIntake_1_2: '1–2L',
      waterIntake_2_3: '2–3L',
      waterIntake_3plus: '3L+',
      hydrationNudge: 'Staying hydrated supports energy, focus, and overall wellness.',
      // Sleep quality enrichment (2026-05-25): mirrors hydration — felt
      // 4-level scale + optional 4-bucket duration. Calm, supportive,
      // non-diagnostic. Other locales fall back to these EN strings until
      // native-speaker QA translates them post-launch.
      sleepQualityQ: 'How did you sleep last night?',
      sleepQuality_excellent: 'Excellent',
      sleepQuality_good: 'Good',
      sleepQuality_fair: 'Fair',
      sleepQuality_poor: 'Poor',
      sleepDurationQ: 'About how long did you sleep?',
      sleepDuration_lt4: 'Less than 4 hours',
      sleepDuration_4_6: '4–6 hours',
      sleepDuration_6_8: '6–8 hours',
      sleepDuration_8plus: '8+ hours',
      sleepNudge: 'Restful sleep helps your body and mind recover, supporting energy, focus, and mood the next day.',
      sleepNudgeRest: 'A gentler day can still be a good day. If you can, give yourself a slower pace and an earlier wind-down tonight.',
      // Activity enrichment (2026-05-25): 4-level felt scale + 6 multi-select
      // types. Encouraging, preventive, non-diagnostic. Other locales fall
      // back to these EN strings until native-speaker QA translates them.
      activityLevelQ: 'How active were you today?',
      activityLevel_very_active: 'Very active',
      activityLevel_moderately: 'Moderately active',
      activityLevel_lightly: 'Lightly active',
      activityLevel_sedentary: 'Mostly sedentary',
      activityTypesQ: 'What kind of activity? (you can pick a few)',
      activityType_walking: 'Walking',
      activityType_gym: 'Gym',
      activityType_stretching: 'Stretching',
      activityType_sports: 'Sports',
      activityType_house_chores: 'House chores',
      activityType_other: 'Other',
      activityNudge: 'Regular movement supports heart health, mood, and long-term wellbeing — every little bit counts.',
      activityNudgeGentle: 'Rest days are part of a healthy rhythm. A short walk or some gentle stretching tomorrow can be a kind next step.',
      // Caffeine intake (2026-05-25): lightweight daily tracker.
      // Educational nudges touch on sleep / hydration / energy balance.
      // Non-diagnostic. Other locales fall back to EN.
      caffeineQ: 'How much caffeine did you consume today?',
      caffeine_none: 'None',
      caffeine_1: '1 cup',
      caffeine_2_3: '2–3 cups',
      caffeine_4plus: '4+ cups',
      caffeineNudge: 'A moderate amount of caffeine can support focus and energy. Pairing it with water and a steady wind-down at night helps protect restful sleep.',
      caffeineNudgeBalance: 'Higher caffeine days can affect sleep and hydration. Try a glass of water between drinks, and consider an earlier last cup so it does not crowd into your evening.',
      // Sugary drinks (2026-05-25): preventive-wellness daily tracker.
      // Educational nudges; non-diagnostic. Other locales fall back to EN.
      sugaryQ: 'How many sugary or fizzy drinks did you have today?',
      sugary_none: 'None',
      sugary_1: '1 drink',
      sugary_2_3: '2–3 drinks',
      sugary_4plus: '4+ drinks',
      sugaryNudge: 'Mostly choosing water, herbal teas, or naturally flavoured drinks supports steady energy and long-term heart and metabolic health.',
      sugaryNudgeBalance: 'Sugary or fizzy drinks can lead to energy dips and add up over time. A glass of water alongside, or swapping one drink for a lower-sugar option, is a kind step.',
      // Medication & pain relief (2026-05-25): strictly educational tracker.
      // Never directive. The "Yes"-variant nudge encourages partnering with
      // a healthcare professional; the baseline mentions keeping personal
      // notes for appointments. No drug names, dosages, brands, or
      // side-effects. Other locales fall back to EN.
      medicationQ: 'Did you take any medication or pain relief today?',
      medication_no: 'No',
      medication_prescribed: 'Yes – prescribed medication',
      medication_otc: 'Yes – over-the-counter medication',
      medication_pain_relief: 'Yes – pain relief medication',
      medicationNoteLabel: 'Would you like to add a note?',
      medicationNotePlaceholder: 'A short note for yourself, only if it helps…',
      medicationNudge: 'Keeping a quiet record of how you feel each day can be a helpful reference for your next conversation with a doctor, pharmacist, or trusted health professional.',
      medicationNudgeYes: 'This log is for your personal awareness — not medical advice. For questions about what you are taking, side effects, or whether something is right for you, please speak with a doctor, pharmacist, or trusted health professional.',
      // Nutrition habits (2026-05-25): 3-level meal balance + optional
      // fruit & veg servings. Encouraging, non-diagnostic, never
      // restrictive. Other locales fall back to EN.
      mealBalanceQ: 'How balanced were your meals today?',
      mealBalance_very_balanced: 'Very balanced',
      mealBalance_moderately: 'Moderately balanced',
      mealBalance_needs_improvement: 'Needs improvement',
      fruitVegQ: 'Fruit & vegetable servings today',
      fruitVeg_0: '0 servings',
      fruitVeg_1_2: '1–2 servings',
      fruitVeg_3_5: '3–5 servings',
      fruitVeg_5plus: '5+ servings',
      nutritionNudge: 'Balanced meals — a mix of vegetables, protein, whole grains, and healthy fats — support steady energy, mood, and long-term wellbeing.',
      nutritionNudgeGentle: 'Some days are off-balance, and that is okay. Adding one piece of fruit or a handful of vegetables to your next meal is a kind, simple next step.',
    },
    // Emotional wellness section (2026-05-25): supportive, non-diagnostic.
    // Strings are intentionally calm and warm. The supportive-variant
    // nudge mentions "trusted people" and "mental health professional"
    // without using clinical labels or risk language. Other locales fall
    // back to these EN strings until native-speaker QA translates them.
    emotional: {
      title: 'Emotional wellness',
      q: 'How are you feeling emotionally today?',
      emotion_calm: 'Calm',
      emotion_happy: 'Happy',
      emotion_stressed: 'Stressed',
      emotion_anxious: 'Anxious',
      emotion_low: 'Low',
      emotion_overwhelmed: 'Overwhelmed',
      noteLabel: 'Would you like to share more?',
      notePlaceholder: 'A few words about today, only if it helps…',
      nudgeSelfCare: 'Taking a moment to notice how you feel is a small act of self-care. A short pause, a few deep breaths, or reaching out to someone you love can be enough.',
      nudgeSupportive: 'Difficult feelings come and go. Be gentle with yourself today. If they feel heavy or persist, please consider reaching out to someone you trust or a mental health professional.',
    },
    mind: {
      title: 'Mind & energy',
      stressQ: 'How is your stress level today?',
      moodQ: 'How is your mood today?',
      energyQ: 'How is your energy level today?',
      low: 'Low',
      moderate: 'Moderate',
      high: 'High',
      neutral: 'Neutral',
      good: 'Good',
      okay: 'Okay',
      // Stress check-in enrichment (2026-05-25): 5-level felt scale +
      // 6-chip optional coping multi-select. Supportive, non-clinical;
      // the high/very-high nudge mentions reaching out to someone you
      // trust without using clinical labels. Other locales fall back to EN.
      stressLevelQ: 'What was your stress level today?',
      stressLevel_very_low: 'Very low',
      stressLevel_mild: 'Mild',
      stressLevel_moderate: 'Moderate',
      stressLevel_high: 'High',
      stressLevel_very_high: 'Very high',
      copingQ: 'Anything that helped today? (you can pick a few)',
      coping_deep_breathing: 'Deep breathing',
      coping_rest: 'Rest',
      coping_exercise: 'Exercise',
      coping_talking: 'Talking to someone',
      coping_meditation: 'Meditation',
      coping_none: 'None',
      stressNudge: 'Short pauses, a few deep breaths, or a quick walk can take the edge off a busy day. Be kind to yourself.',
      stressNudgeSupportive: 'Higher-stress days are part of life — and you do not have to face them alone. Reaching out to someone you trust, or simply naming how you feel, can help. If stress feels heavy or lasting, please consider speaking with a doctor or mental health professional.',
      // Energy level enrichment (2026-05-25): 4-level felt scale.
      // Supportive, non-diagnostic; covers rest, hydration, nutrition,
      // and balance. Other locales fall back to EN.
      energyLevelQ: 'How was your energy level today?',
      energyLevel_very_energetic: 'Very energetic',
      energyLevel_good: 'Good',
      energyLevel_low: 'Low',
      energyLevel_extremely_tired: 'Extremely tired',
      energyNudge: 'Steady energy is built from small daily habits: rest, hydration, balanced meals, and gentle movement. Keep noticing what works for your body.',
      energyNudgeRest: 'Low-energy days happen. Be kind to yourself — a glass of water, a balanced snack, a short walk, or an earlier wind-down tonight can help. If you feel persistently drained, consider checking in with a healthcare professional.',
    },
    // General wellness symptoms (2026-05-25): multi-select chip tracker.
    // Strictly non-diagnostic. The supportive nudge mentions partnering
    // with a healthcare professional for persistent or severe symptoms,
    // without using clinical labels or triage language. Other locales
    // fall back to EN.
    symptoms: {
      title: 'General wellness symptoms',
      q: 'Did you experience any symptoms today?',
      symptom_headache: 'Headache',
      symptom_fatigue: 'Fatigue',
      symptom_dizziness: 'Dizziness',
      symptom_body_pain: 'Body pain',
      symptom_fever: 'Fever',
      symptom_none: 'None',
      nudge: 'This log is for your personal awareness — it is not a medical assessment. Persistent or severe symptoms should be discussed with a healthcare professional.',
      nudgeSupportive: 'Thank you for noting this. If any of these symptoms feel severe, persist over several days, or worry you, please speak with a doctor, pharmacist, or trusted healthcare professional.',
    },
    // Lifestyle habits — multi-select chips with mutually-exclusive
    // "None of the above" behaviour. Tracks common daily lifestyle inputs
    // that influence sleep, energy, mood, heart-health and long-term
    // wellbeing patterns. Strictly observational; never diagnostic.
    lifestyle: {
      title: 'Lifestyle habits today',
      q: 'Did any of the following apply to you today? Select all that apply.',
      smoked_tobacco: 'Smoked tobacco',
      vaping: 'Used vaping products',
      alcohol: 'Consumed alcohol',
      fast_food: 'Ate fast food or takeaway meals',
      energy_drinks: 'Consumed energy drinks',
      none: 'None of the above',
      educational: 'Small daily habits can influence sleep, energy, mood, heart health, and long-term wellbeing. Tracking them helps you better understand patterns in your health over time.',
      disclaimer: 'For personal awareness only — not a medical assessment.',
    },
    day: {
      title: 'Your day',
      routineQ: 'Did you follow your normal routine today?',
      yes: 'Yes',
      no: 'No',
    },
    environment: {
      title: 'Your environment today',
      describeQ: 'How would you describe your environment?',
      comfortable: '😊 Comfortable',
      slightlyUncomfortable: '😐 Slightly uncomfortable',
      challenging: '😓 Challenging',
      affectedQ: 'Did your surroundings affect how you feel?',
      yes: 'Yes',
      no: 'No',
      exposureLabel: 'Exposure (select all that apply)',
      heat: '🌡️ Heat',
      cold: '❄️ Cold',
      noise: '🔊 Noise',
      dustSmoke: '💨 Dust or smoke',
      crowded: '👥 Crowded space',
      none: '✅ None',
    },
    submit: {
      analyzing: 'Analysing your health patterns...',
      cta: 'See My Health Insights',
    },
    toast: {
      savedFull: 'Check-in saved! Your dashboard is updated.',
      savedShort: 'Check-in saved.',
    },
  },
  // The newer structured keys (step1.*, step2.*, healthGoals.*, etc.) live
  // here too so a future OnboardingFlow refactor can graduate to them.
  onboarding: {
    // -- Legacy keys (currently used by OnboardingFlow.jsx) --
    welcome: 'Welcome',
    completeSetup: 'Complete Setup',
    stepProgress: 'Step {{step}} of {{total}}',
    percentComplete: '{{percent}}% complete',
    welcomeToast: 'Welcome to HealthLoop! Your profile is set up.',
    errorAgeRange: 'Please select your age range',
    errorCountry: 'Please select your country',
    errorGoal: 'Please select your primary health goal',
    errorCuisine: 'Please select at least one cuisine preference',
    errorGeneric: 'Failed to complete onboarding. Please try again.',
    step1Title: 'Welcome to HealthLoop! 👋',
    step1Subtitle: "Let's personalize your health journey",
    ageRangeLabel: "What's your age range?",
    genderLabel: 'Gender (Optional)',
    gender_male: 'Male',
    gender_female: 'Female',
    'gender_non-binary': 'Non-binary',
    gender_prefer_not_to_say: 'Prefer not to say',
    step2Title: 'Where are you based? 🌍',
    step2Subtitle: 'This helps us recommend culturally relevant meals',
    countryLabel: 'Select your country',
    country_uk: 'United Kingdom',
    country_nigeria: 'Nigeria',
    country_ghana: 'Ghana',
    country_kenya: 'Kenya',
    country_south_africa: 'South Africa',
    country_usa: 'United States',
    country_canada: 'Canada',
    country_india: 'India',
    country_jamaica: 'Jamaica',
    country_other: 'Other',
    step3Title: "What's your main goal? 🎯",
    step3Subtitle: 'Choose your primary health objective',
    additionalGoals: 'Any other goals? (Optional)',
    goal_weight_loss: 'Weight Loss',
    goalDesc_weight_loss: 'Lose weight healthily',
    goal_weight_maintenance: 'Maintain Weight',
    goalDesc_weight_maintenance: 'Stay at current weight',
    goal_muscle_gain: 'Build Muscle',
    goalDesc_muscle_gain: 'Gain strength & muscle',
    goal_general_wellness: 'General Wellness',
    goalDesc_general_wellness: 'Overall health improvement',
    goal_better_sleep: 'Better Sleep',
    goalDesc_better_sleep: 'Improve sleep quality',
    goal_reduce_stress: 'Reduce Stress',
    goalDesc_reduce_stress: 'Manage stress better',
    goal_increase_energy: 'More Energy',
    goalDesc_increase_energy: 'Feel more energized',
    step4Title: 'Food preferences 🍽️',
    step4Subtitle: 'Select cuisines you enjoy and any dietary restrictions',
    cuisineLabel: 'Preferred Cuisines',
    cuisineHint: 'Select at least one (you can choose multiple)',
    dietaryLabel: 'Dietary Restrictions (Optional)',
    diet_vegetarian: 'Vegetarian',
    diet_vegan: 'Vegan',
    diet_halal: 'Halal',
    diet_kosher: 'Kosher',
    diet_gluten_free: 'Gluten-Free',
    diet_dairy_free: 'Dairy-Free',
    diet_pescatarian: 'Pescatarian',
    step5Title: 'Health conditions 🏥',
    step5Subtitle: 'Optional — helps us provide better recommendations',
    conditionsLabel: 'Do you have any of these conditions?',
    conditionsHint: 'This information is private and helps personalize your experience',
    condition_diabetes: 'Diabetes',
    condition_hypertension: 'High Blood Pressure',
    condition_heart_disease: 'Heart Disease',
    condition_thyroid: 'Thyroid Issues',
    condition_celiac: 'Celiac Disease',
    condition_ibs: 'IBS',
    condition_none: 'None',
    privacyTitle: 'Your data is protected',
    privacyBody: 'Your personal information is encrypted and stored securely. We use this data only to personalise your experience with tailored meal recommendations, health tips, and notifications. Your data will never be shared with third parties without your explicit consent.',
    trackMedication: 'I want to track my medications',
    specialistInterest: "I'm interested in specialist consultations",

    // Cuisine names (used by OnboardingFlow.jsx step 4)
    cuisine_nigerian:      'Nigerian',
    cuisine_ghanaian:      'Ghanaian',
    cuisine_senegalese:    'Senegalese',
    cuisine_kenyan:        'Kenyan',
    cuisine_ethiopian:     'Ethiopian',
    cuisine_moroccan:      'Moroccan',
    cuisine_south_african: 'South African',
    cuisine_jamaican:      'Jamaican',
    cuisine_trinidadian:   'Trinidadian',
    cuisine_lebanese:      'Lebanese',
    cuisine_turkish:       'Turkish',
    cuisine_greek:         'Greek',
    cuisine_italian:       'Italian',
    cuisine_french:        'French',
    cuisine_british:       'British',
    cuisine_indian_south:  'South Indian',
    cuisine_indian_north:  'North Indian',
    cuisine_pakistani:     'Pakistani',
    cuisine_japanese:      'Japanese',
    cuisine_chinese:       'Chinese',
    cuisine_korean:        'Korean',
    cuisine_vietnamese:    'Vietnamese',
    cuisine_thai:          'Thai',
    cuisine_filipino:      'Filipino',
    cuisine_american:      'American',
    cuisine_mexican:       'Mexican',
    cuisine_brazilian:     'Brazilian',
    cuisine_peruvian:      'Peruvian',

    // -- Newer structured keys (available for future OnboardingFlow refactor) --
    welcomeTitle: 'Welcome to HealthLoop! 👋',
    welcomeSubtitle: "Let's personalize your AI-powered preventive health & lifestyle intelligence in a few simple steps",
    stepLabel: 'Step {{current}} of {{total}}',
    skipForNow: 'Skip for now',
    skipToast: 'You can complete onboarding anytime from your profile',
    back: 'Back',
    next: 'Next',
    completing: 'Completing...',
    complete: 'Complete Setup',
    successToast: 'Welcome to HealthLoop! Your profile is now personalized 🎉',
    failureToast: 'Failed to complete onboarding',
  },

  // Dashboard
  dashboard: {
    welcome: 'Welcome back',
    greeting: 'Hello',
    goodMorning: 'Good Morning',
    goodAfternoon: 'Good Afternoon',
    goodEvening: 'Good Evening',
    tagline: 'Small healthy steps create lasting change',
    microcopy: 'Your wellness journey matters.',
    welcomeSubtitleMorning1: 'A calm start to the day.',
    welcomeSubtitleMorning2: 'Glad you stopped by this morning.',
    welcomeSubtitleMorning3: 'Small moments add up.',
    welcomeSubtitleAfternoon1: 'Hope your day feels steady.',
    welcomeSubtitleAfternoon2: 'Welcome back to HealthLoop.',
    welcomeSubtitleAfternoon3: "Take a breath — you're doing well.",
    welcomeSubtitleEvening1: 'Winding down with you.',
    welcomeSubtitleEvening2: 'A gentle close to the day.',
    welcomeSubtitleEvening3: 'Welcome back this evening.',
    title: 'Your Preventive Health Intelligence Platform',
    subtitle: 'Track, learn, and take action before problems escalate',
    summary: 'Your Health Intelligence Summary',
    summaryDesc: 'Real-time insights across prevention, education, engagement, and wellness',
    comprehensiveSupport: 'Comprehensive support across prevention, education, engagement, and wellness',
    todayCheckIn: "Today's Check-In",
    quickActions: 'Quick Actions',
    healthOverview: 'Health Overview',
    recentActivity: 'Recent Activity',
    viewAll: 'View All',
    preventiveHealth: 'Preventive Health',
    preventiveHealthDesc: 'Daily tracking, wellness monitoring & preventive alerts',
    education: 'Education',
    educationDesc: 'Evidence-based insights, health articles & expert guidance',
    engagement: 'Engagement',
    engagementDesc: 'Community challenges, habit tracking & social support',
    wellness: 'Wellness',
    wellnessDesc: 'Smart nutrition, lifestyle balance & holistic well-being',
    todaysInsight: "Today's Insight",
    wellnessScore: 'Wellness Score',
    healthStreak: 'Health Streak',
    days: 'days',
    logHealth: 'Log Health',
    getEducated: 'Get Educated',
    communityChallenge: 'Community Challenge',
    checkWellness: 'Check Wellness',
    verified: 'Verified',
    joinCommunity: 'Join Community',
    personalizedPlan: 'Personalized Plan',
    score: 'Score',
    checkIns: 'check-ins',
    habits: 'habits',
    // Summary Cards
    preventive: 'Preventive',
    healthMetrics: 'Health Metrics',
    trackMetrics: 'Track Metrics',
    startTracking: 'Start tracking',
    logMetrics: 'Log Metrics',
    dailyInsight: 'Daily Insight',
    learnAbout: 'Learn about preventive health today',
    articlesAvailable: 'articles available',
    browseArticles: 'Browse Articles',
    currentStreak: 'Current Streak',
    keepGoing: 'Keep it going!',
    startStreak: 'Start your streak',
    checkIn: 'Check In',
    nutritionPlan: 'Nutrition Plan',
    mealsPlanned: 'meals planned',
    viewPlan: 'View Plan',
    planYourWeek: 'Plan your week',
    // Mood & Wellness
    moodStress: 'Mood & Stress',
    howFeeling: 'How are you feeling?',
    logWellness: 'Log Wellness',
    // Quick Stats
    medications: 'Medications',
    noMeds: 'No meds tracked',
    addMedications: 'Add your medications',
    daysTracked: 'Days Tracked',
    noHabits: 'No habits yet',
    startTrackingHabits: 'Start tracking habits',
    insights: 'Insights',
    noInsights: 'No insights yet',
    completeFirstPlan: 'Complete your first plan',
    noCheckIns: 'No check-ins yet',
    startFirstCheckIn: 'Start your first check-in',
  },

  // Support & Help
  support: {
    title: 'Support',
    subtitle: 'Find answers, contact us, or report an issue',
    backToSettings: 'Back to settings',
    privacyBanner: 'Support requests are handled privately and securely. Your health information stays confidential.',
    section1: 'Section 1',
    section2: 'Section 2',
    section3: 'Section 3',
    section4: 'Section 4',
    navHelpCenter: 'Help Center',
    navContact: 'Contact Support',
    navHealth: 'Health Services',
    navTechnical: 'Technical Support',
    helpCenterTitle: 'Help Center',
    helpCenterSubtitle: 'Search common questions and find quick answers.',
    contactTitle: 'Contact Support',
    contactSubtitle: 'Reach our team through your preferred channel.',
    healthServicesTitle: 'Health Services',
    healthServicesSubtitle: 'Connect with medication tracking and clinical experts.',
    technicalTitle: 'Technical Support',
    technicalSubtitle: 'Report bugs or technical issues with detailed context.',
    searchPlaceholder: 'Search common questions…',
    liveChat: 'Live Chat',
    emailUs: 'Email Us',
    callBack: 'Schedule a callback',
    sendMessage: 'Send a message',
    medicationTracking: 'Medication tracking',
    specialistAccess: 'Specialist access',
    videoConsultation: 'Video consultation',
    reportBug: 'Report a bug',
    yourName: 'Your name',
    yourEmail: 'Your email',
    subject: 'Subject',
    message: 'Message',
    phone: 'Phone',
    description: 'Description',
    submit: 'Submit',
    successTitle: 'Message sent',
    successDescription: "We'll reply within 24–48 hours.",
    healthDisclaimer: 'For medical emergencies, contact local emergency services. HealthLoop is not a substitute for professional medical care.',
  },

  // Smart Nutrition
  nutrition: {
    smartNutrition: 'Smart Nutrition',
    basedOnGoal: 'Based on your goal:',
    personalizedData: 'Personalized based on your recent health data',
    evidenceBased: 'Evidence-based nutrition as part of your preventive health plan',
    todaysMealSuggestions: "Today's Meal Suggestions",
    customize: 'Customize',
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    loadingBreakfast: 'Loading breakfast...',
    loadingLunch: 'Loading lunch...',
    loadingDinner: 'Loading dinner...',
    cal: 'cal',
    protein: 'protein',
    swapMeal: 'Swap Meal',
    healthyOptionsNearYou: 'Healthy Options Near You',
    enableLocation: 'Enable location',
    view: 'View',
    discoverOptions: 'Discover healthy restaurants, grocery stores, and meal prep vendors near you',
    cuisinesSelected: 'cuisines selected',
    cuisineSelected: 'cuisine selected',
    dietaryPrefs: 'dietary prefs',
    dietaryPref: 'dietary pref',
    customizeMeals: 'Customize Meals',
    subtitleAdaptive: 'Culturally adaptive, personalized meal planning for your health goals',
    hideFavorites: 'Hide Favorites',
    viewFavorites: 'View Favorites',
    customizeMealsLegacy: 'Customize meals to match your cultural preferences and dietary needs',
    viewFullPlan: 'View Full Plan',
    exploreRecipes: 'Explore Recipes',
  },

  // Location
  location: {
    permissionTitle: 'Find healthy options near you',
    permissionDesc: 'Used to recommend restaurants, grocery stores, and meal prep vendors aligned with your health goals.',
    howToEnable: 'How to enable:',
    step1: 'Click the lock icon 🔒 in your browser\'s address bar',
    step2: 'Find "Location" permissions',
    step3: 'Change to "Allow"',
    step4: 'Refresh this page',
    refreshPage: 'Refresh Page',
    allowLocation: 'Allow Location',
    notNow: 'Not now',
    denied: 'Location access was blocked. To see nearby healthy options, you\'ll need to enable location permissions.',
    unavailable: 'Location information is currently unavailable. Please check your device settings.',
    error: 'Unable to access your location. Please try again or check browser settings.',
    unsupported: 'Your browser doesn\'t support location services. Try using a modern browser like Chrome, Firefox, or Safari.',
    accessRequired: 'Location Access Required',
  },

  // Women's Health
  womensHealth: {
    title: "Women's Health",
    description: "Track your cycle, log symptoms, and get personalized wellness tips based on your cycle phase",
    cycleTracking: 'Cycle Tracking',
    symptomLogging: 'Symptom Logging',
    phaseInsights: 'Phase Insights',
    logPeriod: 'Log Period',
    logSymptoms: 'Log Symptoms',
    insights: 'Insights',
    tips: 'Health Tips',
    overview: 'Overview',
    history: 'History',
    startTracking: 'Start Tracking Your Cycle',
  },

  // Profile
  profile: {
    title: 'Profile',
    personalInfo: 'Personal Information',
    healthProfile: 'Health Profile',
    preferences: 'Preferences',
    language: 'Language',
    selectLanguage: 'Select Language',
    notifications: 'Notifications',
    privacy: 'Privacy',
    security: 'Security',
    quickAccess: 'Quick Access',
    editProfile: 'Edit Profile',
    viewStats: 'View Stats',
    backToDashboard: 'Back to Home',
    womensHealthTitle: "Women's Health",
    womensHealthDesc: 'Cycle tracking, symptoms & wellness tips',
    healthRemindersTitle: 'Health Reminders',
    healthRemindersDesc: 'Manage your notifications',
    manageRemindersDesc: 'Manage your reminder preferences and notifications',
    healthPatternAlerts: 'Your Health Pattern Alerts',
    supportHelp: 'Support & Help',
    supportHelpDesc: 'Get assistance, report issues, or browse FAQs',
    admin: 'Admin',
    adminDesc: 'View usage metrics and user behavior insights',
    giveFeedback: 'Give Feedback',
    giveFeedbackDesc: 'Share your experience with HealthLoop',
    reportProblem: 'Report a Problem',
    reportProblemDesc: 'Help us fix bugs and improve the app',
    manageHealthInfo: 'Manage your preventive health information for personalized insights',
    basicInformation: 'Basic Information',
    fullName: 'Full Name',
    email: 'Email',
    dateOfBirth: 'Date of Birth',
    gender: 'Gender',
    height: 'Height (cm)',
    weight: 'Weight (kg)',
    medicalConditions: 'Medical Conditions',
    allergies: 'Allergies',
    personalHealth: 'Personal Health',
    addCondition: 'Add a medical condition',
    addAllergy: 'Add an allergy',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    privacyMessage: 'Your health data is protected with confidential care standards',
  },

  settings: {
    title: 'Settings',
    general: 'General',
    account: 'Account',
    appearance: 'Appearance',
    languageSettings: 'Language Settings',
    changeLanguage: 'Change Language',
    languageNote: 'Some new languages are AI-translated and awaiting native review.',
    languageUpdated: 'Language updated successfully',
    starterBadge: 'Starter',
    starterBadgeTooltip: 'Machine-translated — native-speaker review pending.',
    regions: {
      global: 'Global',
      european: 'European Languages',
      middleEastern: 'Middle Eastern Languages',
      asian: 'Asian Languages',
      african: 'African Languages',
    },
  },
  // Global Search modal
  search: {
    triggerLabel: 'Search HealthLoop',
    triggerHint: 'Try "steps", "pharmacy" or "pregnancy"',
    triggerAriaLabel: 'Open global search',
    placeholder: 'Search features, trackers, support…',
    inputAriaLabel: 'Search HealthLoop features',
    modalAriaLabel: 'Search HealthLoop',
    close: 'Close',
    clear: 'Clear',
    clearAriaLabel: 'Clear search',
    recentSearches: 'Recent searches',
    tryFor: 'Try searching for',
    findFeatures: 'Find any feature in seconds.',
    searchAcross: 'Search across',
    and: 'and',
    sections: {
      home: 'Home',
      nutrition: 'Nutrition',
      family: 'Family',
      services: 'Services',
      support: 'Support',
      profile: 'Profile',
    },
    noMatchesFor: 'No matches for "{{query}}"',
    trySimpler: 'Try a simpler word like steps, nutrition or specialist.',
    comingSoonBadge: 'Coming soon',
    escToClose: 'to close',
    resultSingular: '{{count}} result',
    resultPlural: '{{count}} results',
    searchAcrossApp: 'Search across the entire app',
  },
  // Family member card
  familyMember: {
    confirmRemove: 'Remove {{name}} from family health tracking?',
    removed: '{{name}} removed from family',
    removeFailed: 'Failed to remove member',
    viewDetails: 'View Details',
    edit: 'Edit',
    remove: 'Remove',
    yearsSuffix: 'years',
    healthGoalsLabel: 'Health Goals:',
    moreSuffix: 'more',
    activeInsights: 'Active Insights',
    viewAllInsights: 'View all {{count}} insights →',
    viewFullProfile: 'View Full Health Profile',
    lifeStage: {
      infant: 'Infant (0-2)',
      toddler: 'Toddler (2-5)',
      child: 'Child (6-12)',
      adolescent: 'Teen (13-17)',
      adult: 'Adult (18-59)',
      senior: 'Senior (60+)',
      unknown: 'Unknown',
    },
  },

  // Profile (deep extraction)
  profileDeep: {
    backToDashboard: 'Back to dashboard',
    profileCompletion: 'Profile Completion',
    healthSummary: 'Health summary',
    sectionA: 'Section A',
    sectionB: 'Section B',
    sectionC: 'Section C',
    sectionD: 'Section D',
    personalDetails: 'Personal details',
    healthInformation: 'Health information',
    accountSettings: 'Account settings',
    fullName: 'Full name',
    email: 'Email',
    dob: 'Date of birth',
    gender: 'Gender',
    genderPlaceholder: 'e.g., Female, Male, Non-binary',
    height: 'Height (cm)',
    heightPlaceholder: 'e.g., 170',
    weight: 'Weight (kg)',
    weightPlaceholder: 'e.g., 70',
    allergies: 'Allergies',
    allergiesPlaceholder: 'e.g., Peanuts',
    medicalConditions: 'Medical conditions',
    conditionsPlaceholder: 'e.g., Hypertension',
    medications: 'Medications',
    medicationsPlaceholder: 'e.g., Lisinopril 10mg, daily',
    emergencyContact: 'Emergency contact',
    emergencyOptional: '(optional)',
    emergencyPlaceholder: 'e.g., Mom +1-555-0100',
    emergencyNote: 'Only included on a clinician share when you opt in.',
    saveChanges: 'Save changes',
    saving: 'Saving…',
    editProfile: 'Edit profile',
    cancelEditing: 'Cancel editing',
    healthScore: 'Health score',
    streak: 'Streak',
    streakDays: '{{n}} days',
    memberSince: 'Member since',
    remindersActive: 'Reminders active',
    conditionsTracked: 'Conditions tracked',
    nextAgenda: 'Next on your agenda',
    notificationsReminders: 'Notifications & reminders',
    notificationsRemindersDesc: 'Manage health reminder times and channels',
    emailNudges: 'Email nudges',
    emailNudgesDesc: 'Gentle reminders & weekly digests',
    on: 'On',
    paused: 'Paused',
    privacySecurity: 'Privacy & security',
    privacySecurityDesc: 'Update your password to keep your account secure',
    preferencesTitle: 'Preferences',
    preferencesDesc: 'Language, region, and personalization',
    supportHelp: 'Help & Support',
    supportHelpDesc: 'Find answers or contact support',
    sectionAccount: 'Account',
    sectionSupport: 'Support',
    sectionAbout: 'About',
    sectionAdmin: 'Admin',
    privacyPolicy: 'Privacy Policy',
    terms: 'Terms',
    appVersion: 'App version',
    giveFeedback: 'Give feedback',
    giveFeedbackDesc: 'Tell us what to improve',
    reportProblem: 'Report a problem',
    reportProblemDesc: 'Found a bug? Let us know',
    adminInsights: 'Admin insights',
    adminInsightsDesc: 'Engagement analytics',
    adminBadge: 'Admin',
    shareWithClinician: 'Share with a clinician',
    shareWithClinicianDesc: 'Read-only summary · expires automatically · revocable anytime',
    share: 'Share',
    profileMayBeOutOfDate: 'Profile may be out of date',
    profileMayBeOutOfDateDesc: 'Keep your wellness data accurate for sharper insights — even small changes (weight, medications) help us tailor your nudges.',
    updateNow: 'Update now',
    profileUpdateError: "Couldn't update your preference. Please try again.",
    healthLoopUser: 'HealthLoop user',
    updatedLabel: 'Updated',
    noEntries: 'No entries yet.',
    completionComplete: 'Your profile is complete. We can give you our best guidance. 💚',
    completionAlmostThere: 'Almost there. A few more details unlock sharper insights.',
    completionInProgress: 'Complete your profile to unlock better wellness insights.',
    justNow: 'just now',
    minAgo: '{{n}} min ago',
    minsAgo: '{{n}} mins ago',
    hourAgo: '{{n}} hour ago',
    hoursAgo: '{{n}} hours ago',
    dayAgo: '{{n}} day ago',
    daysAgo: '{{n}} days ago',
    checkinDueToday: 'Check-in due today',
    checkinDueTonight: 'Check-in due tonight',
  },

  // SupportHelp (deep extraction)
  supportDeep: {
    backToSettings: 'Back to settings',
    quickNavAria: 'Support sections',
    searchPlaceholder: 'Search for help…',
    searchAria: 'Search frequently asked questions',
    resultsCount: '{{n}} result',
    resultsCountPlural: '{{n}} results',
    questionsCount: '{{n}} questions',
    liveChatTitle: 'Live Chat',
    liveChatDesc: 'Talk to us in real time · Mon–Fri 9am–6pm',
    startChat: 'Start Chat',
    liveChatSoonToast: 'Live chat is launching soon. Until then, email works great.',
    emailUsTitle: 'Email Us',
    emailUsDesc: 'Response within 24–48 hours',
    copy: 'Copy',
    emailCopied: 'Email copied to clipboard.',
    contactFormTitle: 'Contact Form',
    contactFormDesc: 'Send us a message — we’ll reply by email.',
    yourNameLabel: 'Your name *',
    namePlaceholder: 'Jane Doe',
    yourEmailLabel: 'Your email *',
    emailPlaceholder: 'jane@example.com',
    subjectOptional: 'Subject (optional)',
    subjectPlaceholder: 'What can we help with?',
    messageLabel: 'Message *',
    messagePlaceholder: 'Tell us a little about what you need.',
    sending: 'Sending…',
    sendMessage: 'Send Message',
    messageSent: 'Message sent',
    messageSentDesc: "We'll reply within 24–48 hours.",
    callbackTitle: 'Request a Callback',
    callbackDesc: 'We’ll phone you back at a time that works.',
    phoneLabel: 'Phone number *',
    phonePlaceholder: '+1 (555) 123-4567',
    preferredTimeLabel: 'Preferred time (optional)',
    preferredTimePlaceholder: 'e.g. tomorrow afternoon',
    reasonLabel: 'Reason (optional)',
    reasonPlaceholder: 'Briefly, what would you like to discuss?',
    submitting: 'Submitting…',
    requestCallback: 'Request Callback',
    callbackRequested: 'Callback requested',
    callbackRequestedDesc: "We'll be in touch within 24 hours.",
    medicationSupport: 'Medication Support',
    medicationSupportDesc: 'Track prescriptions, refills, and reminders.',
    goToMedications: 'Go to medications →',
    specialistConsultation: 'Specialist Consultation',
    specialistConsultationDesc: 'Connect with a clinician for personalized health guidance.',
    connectExpert: 'Connect with expert →',
    videoConsult: 'Video Consult',
    videoConsultDesc: 'Book a face-to-face video session with a specialist.',
    bookConsultation: 'Book consultation →',
    premium: 'Premium',
    healthDisclaimer: 'HealthLoop supports your day-to-day wellness — it does not replace emergency medical care. If something feels urgent, please contact your local emergency service.',
    categoryLabel: 'Category',
    catBug: 'Bug / Error',
    catPerformance: 'Slow Performance',
    catFeature: 'Feature Not Working',
    catUi: 'Display Issue',
    catOther: 'Other',
    issueTitle: 'Issue title *',
    issueTitlePlaceholder: 'Brief summary',
    detailedDescription: 'Detailed description *',
    detailedDescriptionPlaceholder: 'What happened? What were you doing when it occurred? Steps to reproduce help us a lot.',
    autoAttachNote: 'Device, browser, and viewport info are attached automatically.',
    submitReport: 'Submit Report',
    reportSubmitted: 'Report submitted',
    reportSubmittedDesc: 'Our team is on it.',
    fillAllFields: 'Please fill in all required fields',
    problemReportSubmittedToast: "Problem report submitted. We'll investigate and get back to you soon.",
    messageSentToast: "Message sent. We'll reply within 24–48 hours.",
    callRequestedToast: "Call request received. We'll be in touch within 24 hours.",
    submitFailGeneric: 'Failed to submit. Please try again or email {{email}}.',
    submitFailContact: 'Failed to send. Please email {{email}} directly.',
    comingSoon: 'Coming Soon',
    specialistRoadmapTitle: 'Talk to a Specialist',
    specialistRoadmapSubtitle: 'Human guidance is on the way. These flows will live here so help is always one tap away.',
    talkToSpecialistTitle: 'Talk to a Specialist',
    talkToSpecialistDesc: 'Real-time chat with licensed clinicians for everyday health questions.',
    bookSpecialistTitle: 'Book a Specialist',
    bookSpecialistDesc: 'Schedule a 1:1 session with the right specialist for your goals.',
    liveHealthGuidanceTitle: 'Live Health Guidance',
    liveHealthGuidanceDesc: 'Drop-in video guidance for symptoms, lab results, or treatment plans.',
    mentalWellnessTitle: 'Mental Wellness Support',
    mentalWellnessDesc: 'Confidential support from mental-health professionals when life gets heavy.',
    wellnessNavigationTitle: 'Wellness Navigation',
    wellnessNavigationDesc: 'A guided walkthrough that helps you choose the right next step.',
    pharmacyRoadmapTitle: 'Medication & Pharmacy Support',
    pharmacyRoadmapSubtitle: 'Pharmacy-related help lives inside Support & Help — no extra tab to learn.',
    medicationGuidanceTitle: 'Medication Guidance',
    medicationGuidanceDesc: 'Plain-language guidance on dosage, timing, and common interactions.',
    findNearbyPharmacyTitle: 'Find Nearby Pharmacy',
    findNearbyPharmacyDesc: 'Quickly locate trusted pharmacies in your area when you need a refill.',
    refillReminderTitle: 'Refill Reminder Support',
    refillReminderDesc: 'Gentle reminders so you never run out of important medications.',
    otcWellnessTitle: 'OTC Wellness Advice',
    otcWellnessDesc: 'Curated guidance on safe, evidence-based over-the-counter options.',
  },

  // Care Services — dedicated tab namespace.
  careServices: {
    pageTitle: 'Care Services',
    pageSubtitle: 'Clinical and wellness support designed for your everyday health journey.',

    // Consolidated category cards (replaces 12 individual placeholder cards
    // — see 2026-06-08 UX refinement for the rationale). Each card carries
    // an optional `features` array of short sub-feature labels surfaced as
    // pills inside the card.
    healthcareServicesTitle: 'Healthcare Services',
    healthcareServicesSubtitle: 'Clinical, pharmacy, mental wellness, and nutrition support — launching as HealthLoop scales.',
    roadmapTitle: 'Roadmap',
    roadmapSubtitle: 'Future categories that extend the HealthLoop platform.',

    specialistConsultationTitle: 'Specialist Consultation',
    specialistConsultationDesc: 'Connect with qualified healthcare professionals through chat, video guidance, and appointment booking.',
    specialistConsultationFeature1: 'Specialist chat',
    specialistConsultationFeature2: 'Video consultations',
    specialistConsultationFeature3: 'Appointment scheduling',

    pharmacySupportTitle: 'Pharmacy Support',
    pharmacySupportDesc: 'Medication guidance, pharmacy access, and refill support in one place.',
    pharmacySupportFeature1: 'Medication guidance',
    pharmacySupportFeature2: 'Nearby pharmacy locator',
    pharmacySupportFeature3: 'Refill reminders',

    mentalWellnessTitle: 'Mental Wellness Support',
    mentalWellnessDesc: 'Confidential emotional and mental wellbeing support.',

    wellnessGuidanceTitle: 'Wellness Guidance',
    wellnessGuidanceDesc: 'Evidence-based wellness advice and recommended next steps.',
    wellnessGuidanceFeature1: 'OTC wellness advice',
    wellnessGuidanceFeature2: 'Next-step navigation',

    healthyFoodNetworkTitle: 'Healthy Food Network',
    healthyFoodNetworkDesc: 'Healthy meals, trusted wellness food partners, and fresh produce access.',
    healthyFoodNetworkFeature1: 'Healthy meal delivery',
    healthyFoodNetworkFeature2: 'Farm partnerships',
    healthyFoodNetworkFeature3: 'Wellness food partners',

    // ----- Legacy keys (kept for backward compatibility across 22 locales).
    // The new consolidated UI does not reference these; do not remove until
    // post-beta when locale files get their next QA pass.
    futureWellnessTitle: 'Future Wellness Features',
    futureWellnessSubtitle: 'A preview of what\'s on HealthLoop\'s preventive-wellness roadmap.',
    futureFeatureBadge: 'Future Feature',
    futurePersonalizedMealsTitle: 'Personalized Meal Plans',
    futurePersonalizedMealsDesc: 'Wellness-led meal plans tuned to your goals, allergies, and culture.',
    futureHealthyDeliveryTitle: 'Healthy Meal Delivery',
    futureHealthyDeliveryDesc: 'Curated delivery from health-conscious kitchens and partners.',
    futureFarmFreshTitle: 'Farm-Fresh Wellness Foods',
    futureFarmFreshDesc: 'Direct access to local farms and seasonal fresh-produce partners.',
    futureFoodPartnershipsTitle: 'Wellness Food Partnerships',
    futureFoodPartnershipsDesc: 'Curated relationships with healthy kitchens, grocers, and wellness brands.',
    futureNutritionCoachingTitle: 'Nutrition Coaching',
    futureNutritionCoachingDesc: 'One-to-one guidance from credentialed nutrition coaches.',
    futureFamilyWellnessTitle: 'Family Wellness Plans',
    futureFamilyWellnessDesc: 'Shared plans that keep the whole household on track.',
    futureWellnessPlusTitle: 'Wellness+ Membership',
    futureWellnessPlusDesc: 'A premium tier for personalised guidance, priority support, and deeper insights.',
    futurePharmacyPartnersTitle: 'Pharmacy Partnerships',
    futurePharmacyPartnersDesc: 'Trusted pharmacy connections for medication guidance and refills.',
    futureAdvancedSpecialistTitle: 'Advanced Specialist Care',
    futureAdvancedSpecialistDesc: 'Coordinated access to specialists, second opinions, and care navigation.',
    futureWellnessMarketplaceTitle: 'Wellness Marketplace',
    futureWellnessMarketplaceDesc: 'A curated marketplace of vetted wellness products and services.',
    futureVendorCollabTitle: 'Vendor Collaboration Systems',
    futureVendorCollabDesc: 'Onboarding and quality systems for healthy food and wellness partners.',
  },

  // Dashboard (deep extraction additions)
  dashboardDeep: {
    recsSummary: {
      excellent: 'You\'re set for today — keep the rhythm.',
      good: '{{done}} of {{total}} done • a couple of light steps left',
      improvable: '{{done}} of {{total}} done • two improvements would lift your score',
      needsAttention: '{{done}} of {{total}} done • start with one priority below'
    },
    priorityBadge: 'Priority',
    factor: {
      water: 'hydration',
      meals: 'nutrition',
      steps: 'movement',
      sleep: 'sleep'
    },
    rec: {
      water: {
        priority: {
          title: 'Hydration is low — start with a glass now',
          description: 'Your hydration is well below target. A glass within the next hour helps reset.'
        },
        improvement: {
          title: 'Lift your hydration today',
          description: 'Aim for 6–8 glasses of water across the day to stay energised.'
        },
        maintenance: {
          title: 'Stay hydrated',
          description: 'Keep up your steady water intake through the rest of the day.'
        }
      },
      meals: {
        priority: {
          title: 'A balanced meal is your next step',
          description: 'Today\'s nutrition is light. A meal with vegetables and protein soon will help.'
        },
        improvement: {
          title: 'Round out your nutrition today',
          description: 'Include fruits, vegetables, or a balanced meal in your next sitting.'
        },
        maintenance: {
          title: 'Maintain balanced nutrition',
          description: 'Keep including fruits and vegetables — you\'re doing this well.'
        }
      },
      steps: {
        priority: {
          title: 'Movement is low — a short walk now helps',
          description: 'Even 10 minutes of movement will lift your energy through the evening.'
        },
        improvement: {
          title: 'Add some movement today',
          description: 'Aim for 5,000–10,000 steps across the day, in any rhythm that suits you.'
        },
        maintenance: {
          title: 'Stay active',
          description: 'Keep moving gently through the day to maintain your rhythm.'
        }
      },
      sleep: {
        priority: {
          title: 'Recovery needs attention tonight',
          description: 'Wind down a little earlier — aim for 7–8 hours to reset your baseline.'
        },
        improvement: {
          title: 'Aim for restful sleep tonight',
          description: 'Target 7–8 hours of rest to support tomorrow.'
        },
        maintenance: {
          title: 'Protect your sleep',
          description: 'Keep up your 7–8 hours tonight — recovery is where the work compounds.'
        }
      }
    },
    insight: {
      allOnTrack: 'Hydration, nutrition, movement, and rest — all on track today. This is the kind of day that builds long-term health.',
      singlePending: 'Your {{factor}} is the only factor still pending today. A small step now keeps the rhythm intact.',
      pairLowScore: 'Your {{factors}} are both low today. Lifting either is the fastest way to feel better by evening.',
      pairOk: 'Your {{factors}} both need a small lift today. They tend to move together — improving one often helps the other.',
      pair: {
        'water+sleep': {
          v1: 'Hydration and sleep are the two factors most often linked to focus. Lifting both today usually steadies tomorrow too.',
          v2: 'When hydration and sleep are low together, energy follows. A glass of water now and an early wind-down tonight will help.'
        },
        'water+meals': {
          v1: 'Hydration and balanced meals are still pending today. Together, they\'re the easiest lift for steady energy.',
          v2: 'Light meals plus low water often means dips by mid-afternoon. A small adjustment to either changes the curve.'
        },
        'water+steps': {
          v1: 'Hydration and movement reinforce each other — water primes you to move, movement keeps you alert.',
          v2: 'A glass of water and a short walk are the two cheapest interventions for the rest of your day.'
        },
        'meals+sleep': {
          v1: 'Nutrition shapes how well you sleep, and sleep shapes how well you eat. Today is a chance to lift both.',
          v2: 'Balanced meals and 7–8 hours of rest is the simplest pair for resilience. Aim for both today.'
        },
        'meals+steps': {
          v1: 'Movement and balanced meals together are one of the strongest signals for long-term health.',
          v2: 'A 10-minute walk after your next meal nudges both of these in your favour at once.'
        },
        'steps+sleep': {
          v1: 'Activity earlier in the day usually leads to deeper sleep at night. Both are pending — they help each other.',
          v2: 'Movement now, rest tonight. They reinforce each other more than people realise.'
        }
      }
    },
    healthProfile: 'Health Profile',
    notifications: 'Notifications',
    familyAccounts: 'Family Accounts',
    settings: 'Settings',
    logout: 'Logout',
    allCaughtUp: "You're all caught up.",
    weekInReview: 'Your week in review',
    dismissNotification: 'Dismiss notification',
    dismissWeeklyDigest: 'Dismiss weekly digest',
    notificationsAria: 'Notifications',
    profileMenuAria: 'Open profile menu',
    loadingWellness: 'Loading your wellness summary',
    nextTip: 'Next tip',
    todayAtGlance: 'Today at a glance',
    healthScoreLabel: 'Health Score',
    currentStreakLabel: 'Current Streak',
    todaysProgress: "Today's Progress",
    insightForYou: 'Insight for you',
    checkMyHealthRisk: 'Check My Health Risk',
    quickAccess: 'Quick Access',
    exploreCareServicesTitle: 'Explore Care Services',
    exploreCareServicesSubtitle: 'Specialist care · pharmacy · wellness — all in one place.',
    familyCare: 'Family Care',
    healthLibrary: 'Health Library',
    myHealthData: 'My Health Data',
    askQuestion: 'Ask a Question',
    dashLoadError: "Couldn't load your dashboard. Please try again.",
    taskUpdateError: "Couldn't update task. Please try again.",
    perfectDay: 'Perfect day! 🎉 Streak extended.',
    letsStart: "Let's start",
    yourNextStepToday: 'Your next step today',
    completeDailyCheck: 'Complete your daily health check',
    startDailyCheck: 'Start Daily Check',
    viewAll: 'View all',
    graceProtected: 'Grace protected',
    todaysHealthRecommendations: "Today's Health Recommendations",
    updateViaCheck: 'Update via daily check',
    healthInsight: 'Health Insight',
    riskCheckSubtext: 'Get personalised health insights in seconds',
    foodDiet: 'Food & Diet',
    healthScoreNew: 'Health Score · New',
    letsBuildScore: 'Let’s build your health score together',
    oneHealthyHabitAtATime: 'One healthy habit at a time.',
    nextCheck: 'Next check:',
    todayAt8pm: 'today at 8:00 PM',
    wellnessTipForYou: "Today's Wellness Tip · For you",
    wellnessTipForFactor: "Today's Wellness Tip · For your {{factor}}",
    logged: 'Logged',
    gotIt: 'Got it 👍',
    gladToSeeYou: 'We’re glad to see you today 💚',
    takesLessThan60Sec: 'Takes less than 60 seconds',
    scoreReadyWhenYou: 'Ready when you are',
    scoreCompleteFirstCheck: 'Complete your first wellness check to unlock personalised insights.',
    scoreExcellent: 'Excellent',
    scoreSupportExcellent: "You're in a great state today. Keep it up.",
    scoreGood: 'Good',
    scoreSupportGood: "You're doing well. Maintain your healthy habits.",
    scoreModerate: 'Moderate',
    scoreSupportModerate: "You're doing okay, but a few areas need attention.",
    scoreNeedsAttention: 'Needs Attention',
    scoreSupportNeedsAttention: 'Your health needs attention today. Small changes can help improve it.',
  },

  // Email verification banner
  emailVerify: {
    bannerWithEmail: 'Please verify your email ({{email}}) so we can keep your account secure and deliver your daily nudges.',
    bannerNoEmail: 'Please verify your email so we can keep your account secure and deliver your daily nudges.',
    sending: 'Sending…',
    resend: 'Resend email',
    sentToast: 'Verification email sent. Please check your inbox.',
    sendFailToast: 'Could not send verification email. Try again later.',
  },

  // Forgot Password page
  forgotPassword: {
    title: 'Forgot Password?',
    subtitle: "No worries! Enter your email and we'll send you reset instructions.",
    emailLabel: 'Email Address',
    emailPlaceholder: 'your.email@example.com',
    sendResetLink: 'Send Reset Link',
    sending: 'Sending…',
    backToLogin: 'Back to Login',
    resendLink: 'Resend email',
    successTitle: 'Check Your Email',
    successBody: 'If an account exists for {{email}}, we’ve sent a password reset link.',
    linkExpires: 'The link will expire in 30 minutes.',
    successToast: 'Check your email for reset instructions',
    errEmptyEmail: 'Please enter your email address',
    errInvalidEmail: 'Please enter a valid email address',
    errNetwork: "Couldn't reach the server. Check your connection and try again.",
    errGeneric: "Couldn't send reset email. Please try again or contact support.",
  },

  // Reset Password page
  resetPassword: {
    title: 'Set New Password',
    subtitle: 'Create a strong password for your account',
    newPasswordLabel: 'New Password',
    newPasswordPlaceholder: 'Enter new password',
    confirmPasswordLabel: 'Confirm Password',
    confirmPasswordPlaceholder: 'Confirm new password',
    requirementsHint: 'Must be at least 8 characters with uppercase, lowercase, and number',
    resetPassword: 'Reset Password',
    resetting: 'Resetting…',
    successTitle: 'Password Reset Successfully!',
    successBody: 'You can now log in with your new password.',
    successToast: 'Password reset successfully!',
    goToLogin: 'Go to Login',
    invalidLinkTitle: 'Invalid Reset Link',
    invalidLinkBody: 'This password reset link is invalid or has expired.',
    requestNewLink: 'Request New Link',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    errInvalidLink: 'Invalid reset link',
    errMismatch: 'Passwords do not match',
    errTooShort: 'Password must be at least 8 characters',
    errExpiredToken: 'This reset link has expired or is invalid. Please request a new one.',
    errNetwork: "Couldn't reach the server. Check your connection and try again.",
    errGeneric: 'Failed to reset password. Please try again.',
  },

  // Daily wellness tip pool (rotated per day, browsable by user)
  tips: {
    water1: 'Drinking water within 30 minutes of waking up may improve hydration and energy levels.',
    water2: 'A glass of water before each meal is a gentle way to stay ahead of thirst.',
    water3: 'Adding a slice of lemon or a few mint leaves can make hydration feel like a treat.',
    meals1: 'Adding one extra serving of vegetables a day supports gut and heart health.',
    meals2: 'A handful of nuts or seeds with breakfast steadies your energy through the morning.',
    meals3: 'Whole fruit beats juice — the fibre keeps your blood sugar gentler.',
    steps1: 'A 10-minute walk after meals helps your body process sugars more gently.',
    steps2: 'A 5-minute morning stretch can ease tension and set a calm tone for your day.',
    steps3: 'Taking the stairs even once a day quietly builds heart strength over weeks.',
    sleep1: 'Dimming lights an hour before bed helps your body wind down for deeper sleep.',
    sleep2: 'A consistent wake time — even on weekends — anchors a steadier sleep cycle.',
    sleep3: '10 minutes of morning sunlight helps regulate your sleep rhythm naturally.',
    general1: 'Two minutes of slow breathing can lower stress and steady your focus.',
    general2: 'Notice one moment of gratitude today — small mental wins compound, too.',
    general3: 'You showed up today. That is the habit that makes every other habit easier.',
  },

  // Habit factor labels (used in wellness-tip eyebrow "For your {factor}")
  factors: {
    hydration: 'hydration',
    nutrition: 'nutrition',
    movement: 'movement',
    sleep: 'sleep',
  },

  // Nutrition (deep extraction additions)
  nutritionDeep: {
    mealOptionsLoadFail: 'Failed to load meal options',
    prefsSaved: 'Food preferences saved! Refreshing meals...',
    prefsSaveFail: 'Failed to save preferences',
    newOptionsFail: 'Failed to load new options',
    invalidZip: 'Please enter a valid 5-digit zip code',
    noVendorsArea: "No vendors available in this area yet. We're expanding coverage!",
    vendorsFetchFail: 'Failed to fetch vendors',
    selectVendorFirst: 'Please select a delivery vendor first',
    removedFromCart: 'Removed from cart',
    orderPlaced: 'Order placed successfully! 🎉',
    enterZipFirst: 'Please enter your zip code in the delivery section first',
    mealNotAvailable: 'This meal is not available for delivery in your area yet',
    mealUnavailable: 'This meal is currently unavailable from nearby vendors',
    vendorOptionsFail: 'Failed to load vendor options',
    favoriteSaveFail: 'Failed to save favorite',
    removedFromFavorites: 'Removed from favorites',
    favoriteRemoveFail: 'Failed to remove favorite',
    cartEmpty: 'Your cart is empty',
    noMealOptions: 'No meal options available',
    loadingOptions: 'Loading meal options...',
    selectPreferences: 'Select Your Food Preferences',
    mealBuilder: 'Meal Builder',
    myFavorites: 'My Favorites',
    myFavoriteMeals: 'My Favorite Meals',
    noFavoritesYet: 'No favorite meals yet!',
    price: 'Price',
    delivery: 'Delivery',
    deliveryFee: 'Delivery Fee',
    distance: 'Distance',
    subtotal: 'Subtotal',
    total: 'Total',
    orderNumber: 'Order Number',
    vendor: 'Vendor',
    totalAmount: 'Total Amount',
    estimatedDelivery: 'Estimated Delivery',
    whyThisMeal: 'Why this meal',
    selectThisMeal: 'Select This Meal',
    swapMeal: 'Swap Meal',
    orderThisMeal: 'Order This Meal',
    notAvailableNearYou: 'Not available near you',
    tryAdjustingPrefs: 'Try adjusting your food preferences to see meal suggestions',
    myMealPlanSummary: 'My Meal Plan Summary',
    saveCompletePlan: 'Save Complete Plan',
    yourFoodPreferences: 'Your Food Preferences',
    editPreferences: 'Edit Preferences',
    healthyMealDelivery: 'Healthy Meal Delivery',
    enableDelivery: 'Enable Delivery',
    enterZipCode: 'Enter Your Postcode / ZIP to See Available Meals',
    enterPostcode: 'Enter Your Postcode / ZIP Code',
    postcodePlaceholder: 'e.g., MK10 9XX or 10001',
    postcodePartialHint: 'Please enter your full postcode for more accurate local results.',
    noLocalPartnersFallback: 'No local partners found yet. Showing culturally relevant ingredient suggestions instead.',
    noLivePartnersFallback: 'No live HealthLoop food partners are active in your area yet.',
    culturalNutritionTitle: 'Healthy Shopping Guidance',
    culturalNutritionSubtitle: 'General, culturally inclusive ideas for healthy shopping — educational only, not a live vendor or delivery directory.',
    shoppingGuidanceScope: 'Certified local vendor listings are not available yet. You can still explore general healthy shopping guidance below.',
    nearbyDiscoveryComingSoon: 'Nearby wellness · coming soon',
    viewIngredientGuidance: 'View Guidance',
    popularWellnessTitle: 'Popular Wellness Foods',
    popularWellnessIntro: 'Culturally relevant ingredients and the kinds of healthy food environments worth exploring.',
    subsectionPopularFoodsTitle: 'Everyday Wellness Foods',
    subsectionCulturalIngredientsTitle: 'Cultural Ingredient Suggestions',
    subsectionHealthySwapsTitle: 'Healthy Swap Ideas',
    subsectionLocalEnvironmentTitle: 'Healthy Food Environments',
    subsectionLocalEnvironmentIntro: 'Types of places where people often find culturally relevant, healthy options.',
    // Section E — Healthy Stores & Wellness Markets (educational only)
    healthyStoresTitle: 'Healthy Stores & Wellness Markets',
    healthyStoresIntro: 'Culturally inclusive store types worth looking for — for ideas, not delivery.',
    healthyStoresGuidanceTitle: 'Healthy shopping guidance',
    healthyStoresDisclaimer: 'HealthLoop shares general wellness and healthy shopping inspiration only — not medical or dietary treatment advice.',
    healthyStores: {
      organic:        { type: 'Organic grocery stores',          description: 'Fresh, pesticide-free produce and minimally processed pantry staples.',                 tags: ['Whole-foods', 'Fresh produce'] },
      farmers:        { type: "Farmers' markets",                description: 'Locally grown seasonal fruit, vegetables, and traditional produce direct from growers.', tags: ['Local', 'Seasonal'] },
      african:        { type: 'African fresh produce stores',    description: 'Yams, plantains, leafy greens, palm nut and West / East African staples.',              tags: ['Cultural', 'Fresh produce'] },
      caribbean:      { type: 'Caribbean natural food markets',  description: 'Callaloo, cassava, scotch bonnet, ackee, breadfruit and island staples.',                tags: ['Cultural', 'Whole-foods'] },
      asian:          { type: 'Asian vegetable supermarkets',    description: 'Bok choy, daikon, lotus root, tofu, fresh herbs and pan-Asian staples.',                tags: ['Cultural', 'Fresh produce'] },
      halal:          { type: 'Halal healthy food markets',      description: 'Halal-certified lean proteins, fresh produce and traditional spices.',                  tags: ['Halal', 'Cultural'] },
      mediterranean:  { type: 'Mediterranean whole-food markets', description: 'Olive oil, legumes, fresh herbs, whole grains and Mediterranean staples.',              tags: ['Cultural', 'Whole-foods'] },
      wellness:       { type: 'Wellness-focused grocery shops',  description: 'Curated whole foods, plant-based options and clean-label pantry items.',                tags: ['Whole-foods', 'Plant-based'] },
    },
    healthyShoppingTips: [
      'Shop the perimeter first — fresh produce, lean proteins and dairy alternatives usually live around the edges.',
      'Favour whole, single-ingredient foods over heavily processed items.',
      'Choose seasonal local produce — usually fresher, more affordable and more nutrient-dense.',
      'Ask vendors about origin and growing methods — many will happily share.',
    ],
    findLocalTitle: 'Local Healthy Options',
    findLocalSubtitle: 'Discover nearby healthy vendors, stores, and culturally relevant ingredients in your area.',
    findOptions: 'Find Options',
    searching: 'Searching…',
    nearbyVendorsFound: '{{count}} nearby healthy options found',
    orderHintEnabled: 'Tap "Order This Meal" on any meal card below to see vendor pricing.',
    orderHintDisabled: 'Turn on Enable Delivery above to order any of these directly through the meal cards.',
    localIngredientTitle: 'Local Ingredient Availability',
    localIngredientIntro: 'Culturally relevant staples and where to source them locally.',
    lookFor: 'Look for',
    noVendorsFallback: 'No certified delivery vendors here yet — the ingredient guidance above still works at any local grocery or farmers\' market.',
    showingMealsFrom: 'Showing meals from',
    sectionSnapshot: 'Daily Nutrition Snapshot',
    sectionSnapshotSubtitle: 'A quick look at hydration, calories, and meal balance for today.',
    snapshotHydration: 'Hydration',
    snapshotHydrationHint: 'Aim for 6–8 glasses today.',
    snapshotCalories: 'Calories',
    snapshotCaloriesHint: 'Tracking coming soon.',
    snapshotBalance: 'Meal Balance',
    mealsLogged: 'meals selected',
    snapshotTip: 'Wellness Tip',
    snapshotTipHint: 'Add one extra vegetable to your next meal.',
    sectionRecommendations: 'Smart Recommendations',
    sectionRecommendationsSubtitle: 'Culturally relevant ingredient guidance and healthy swap ideas.',
    sectionGoals: 'Nutrition Goals',
    sectionGoalsSubtitle: 'Track outcomes that matter to you.',
    goalWeight: 'Weight Support',
    goalWeightDesc: 'Meals tuned for a sustainable weight goal.',
    goalEnergy: 'Energy Support',
    goalEnergyDesc: 'Steady fuel for long, productive days.',
    goalImmunity: 'Immunity Support',
    goalImmunityDesc: 'Antioxidant-rich choices to stay resilient.',
    goalFamily: 'Family Nutrition',
    goalFamilyDesc: 'Kid-friendly and maternal options included.',
    sectionGuidance: 'Meal Guidance',
    sectionGuidanceSubtitle: 'Breakfast, lunch, dinner, and snack ideas you can swap freely.',
    futureNutritionTitle: 'Future Nutrition Support',
    futureNutritionBody: 'Personalized wellness nutrition, healthy meal partnerships, and family-focused food support are coming to HealthLoop in future updates.',
    comingSoon: 'Coming Soon',
    coverageLocalStores: 'Local healthy stores',
    coverageLocalFarms: 'Local farms',
    coverageCertifiedVendors: 'Certified vendors',
    coverageFreshProduce: 'Fresh produce',
    medWellnessFutureTitle: 'Medication & Wellness Support',
    medWellnessFutureDesc: 'Pairs your nutrition plan with medication-aware guidance. Real flow lives under Support & Help.',
    pharmacyAssistFutureTitle: 'Pharmacy Assistance',
    pharmacyAssistFutureDesc: 'Find nearby pharmacies and refill reminders right alongside your meals. Real flow lives under Support & Help.',
    savePreferences: 'Save Preferences',
    quickAccessSavedMeals: 'Quick access to your saved meals',
    saveMealsFromBuilder: 'Save meals from the Meal Builder tab',
    chooseVendor: 'Choose a vendor to order this meal from',
    yourCart: 'Your Cart',
    addMealsToStart: 'Add meals from the options below to get started',
    orderPlacedDesc: 'Your healthy meal order has been placed successfully',
    continueShopping: 'Continue Shopping',
    trackOrder: 'Track Order',
  },

  // Messages & Notifications
  messages: {
    success: 'Success!',
    error: 'Error',
    saved: 'Saved successfully',
    updated: 'Updated successfully',
    deleted: 'Deleted successfully',
    confirmDelete: 'Are you sure you want to delete this?',
    networkError: 'Network error. Please try again.',
    somethingWrong: 'Something went wrong',
    failedToLoad: 'Failed to load dashboard',
    offlineMode: 'Offline mode - showing limited data',
    locationDenied: 'Location access denied. Check browser settings to enable.',
    locationUnavailable: 'Location information unavailable',
    locationTimeout: 'Location request timed out. Please try again.',
    locationError: 'Unable to access location',
    locationGranted: 'Location access granted! Showing nearby options',
    locationNotSupported: 'Location services not supported by your browser',
    requestingLocation: 'Requesting location access...',
  },

  // Trust & Credibility
  trust: {
    medicalDisclaimer: 'Medical Disclaimer',
    privacySecure: 'Your data is private & secure',
    dataEncrypted: 'All data is encrypted and confidential',
    notMedicalAdvice: 'Not medical advice',
    consultProvider: 'Always consult your healthcare provider',
    evidenceBased: 'Evidence-based guidance',
    generalEducation: 'General health education',
    preventiveGuidance: 'Preventive guidance',
    lifestyleSupport: 'For awareness and lifestyle support',
    lastUpdated: 'Last updated',
    expertReviewed: 'Expert reviewed',
    aiGenerated: 'AI-generated insight',
    disclaimerShort: 'HealthLoop provides health education and is not a substitute for professional medical advice.',
    disclaimerFull: 'HealthLoop supports preventive wellness and early awareness. We provide health education, habit tracking, and wellness insights. This is not a substitute for licensed medical care. Always consult qualified healthcare providers for medical advice, diagnosis, or treatment.',
    trustBanner: {
      title: 'HealthLoop is here to support your wellness journey',
      description: 'We provide general health education and preventive guidance based on trusted sources. This is not a substitute for professional medical advice. Always consult with qualified healthcare providers for medical decisions.',
    },
  },

  // Health Features
  health: {
    preventiveHealth: 'Preventive Health Tracker',
    educationHub: 'Education Hub',
    engagementWidget: 'Engagement Widget',
    wellnessCheckIn: 'Wellness Check-In',
    todaysMetrics: "Today's Metrics",
    bloodPressure: 'Blood Pressure',
    heartRate: 'Heart Rate',
    weight: 'Weight',
    sleep: 'Sleep',
    waterIntake: 'Water Intake',
    exercise: 'Exercise',
    logMetric: 'Log Metric',
    viewHistory: 'View History',
  },

  // AI Health Insights
  aiInsights: {
    title: 'AI Health Insights',
    comingSoon: 'Your insights are gathering',
    comingSoonDesc: 'Complete a daily check-in or two and we\'ll surface gentle, personalised insights here.',
    learnMore: 'Learn more',
    evidenceSources: {
      WHO: 'Based on WHO guidelines',
      NHS: 'Aligned with NHS recommendations',
      CDC: 'Supported by CDC guidelines',
      RESEARCH: 'Based on peer-reviewed research',
      CLINICAL: 'Backed by clinical studies',
    },
  },

  // Daily Health Insight
  dailyInsight: {
    title: 'Daily Health Insight',
    gettingStarted: 'Getting Started',
    focus: 'Focus',
    quickInsight: 'Quick Insight',
    simpleExplanation: 'Simple Explanation',
    realLifeImpact: 'Real-Life Impact',
    yourActionStep: 'Your Action Step',
    basedOnYour: 'Based on your',
    personalizedFor: 'Personalized for you based on your recent health data',
    getNewInsight: 'Get new insight',
    newInsightLoaded: 'New insight loaded! 💡',
    failedToRefresh: 'Failed to refresh insight',
    priority: {
      high: "Let's Focus Here",
      medium: 'Worth Noting',
    },
  },

  // Preventive Alerts
  preventiveAlerts: {
    noAlertsTitle: 'No health alerts at this time',
    noAlertsDesc: "You're maintaining healthy habits. Keep it up!",
    whatYouCanDo: 'What you can do:',
    viewMealSuggestions: 'View Personalized Meal Suggestions',
  },

  // Engagement Widget
  engagement: {
    currentStreak: 'Current Streak',
    bestStreak: 'Best Streak',
    thisWeek: 'This Week',
    exerciseDays: 'Exercise Days',
    hydrationDays: 'Hydration Days',
    healthMetricsLogged: 'Health Metrics Logged',
    weeklyGoalsProgress: 'Weekly Goals Progress',
    completed: 'completed',
    days: 'days',
    keepGoing: 'Keep going!',
    personalRecord: 'Personal record!',
    goalMet: 'Goal met!',
  },

  // Reminders
  reminders: {
    title: 'Health Reminders',
    subtitle: 'Stay on track with personalized health notifications',
    breakfast: 'Breakfast Reminder',
    lunch: 'Lunch Reminder',
    dinner: 'Dinner Reminder',
    water: 'Water Intake',
    movement: 'Movement Break',
    sleep: 'Sleep Reminder',
    wellness: 'Wellness Check-In',
    healthLogging: 'Health Logging',
    annualCheckup: 'Annual Checkup',
    enable: 'Enable',
    disable: 'Disable',
    customize: 'Customize Time',
    stayConsistent: 'Stay Consistent with Smart Reminders',
    setupFriendly: 'Set up friendly reminders to help you build healthy habits.',
    customizeWhen: 'You can customize when and how often you receive each reminder.',
    supportive: "Don't worry - we'll keep them supportive and non-intrusive! 💚",
    noPreferences: 'No Reminder Preferences Found',
    noPreferencesDesc: "We couldn't load your reminder settings. This might be your first time here, or there was an issue loading your preferences.",
    retryLoading: 'Retry Loading',
    saveSettings: 'Save Settings',
    saving: 'Saving...',
  },

  // Empty States
  empty: {
    startJourney: 'Start Your Wellness Journey',
    startJourneyDesc: "Start your health journey today! Share how you're feeling to receive personalized insights and track your wellness over time.",
    completeFirstCheckIn: 'Complete First Check-in',
    stayOnTrack: 'Stay On Track',
    stayOnTrackDesc: 'Stay consistent with your routine 💊 Add your medications to get gentle reminders and support.',
    addFirstMedication: 'Add First Medication',
    learnMedicationSafety: 'Learn About Medication Safety',
    buildMealLibrary: 'Build Your Meal Library',
    buildMealLibraryDesc: 'No favorite meals yet. Save meals you love for quick access and easier meal planning.',
    exploreMealOptions: 'Explore Meal Options',
    insightsAwait: 'Your Insights Await',
    insightsAwaitDesc: 'Complete daily check-ins and track your wellness to unlock personalized health insights and pattern analysis.',
    startDailyCheckIn: 'Start Daily Check-in',
    learnAboutInsights: 'Learn About Insights',
    buildingStory: 'Building Your Wellness Story',
    buildingStoryDesc: 'No historical data yet. Your wellness trends and patterns will appear here as you complete daily check-ins.',
    completeCheckIn: 'Complete Check-in',
    tipComplete7Days: 'Tip: Complete check-ins for 7 days to see meaningful trends and patterns.',
    startTrackingHabits: 'Start Tracking Healthy Habits',
    startTrackingHabitsDesc: 'No habits tracked yet. Small daily actions create lasting wellness changes. Begin building your healthy routine today.',
    trackFirstHabit: 'Track First Habit',
    getMoving: 'Get Moving',
    getMovingDesc: 'No activity logged yet. Even 10 minutes of movement can boost your mood and energy.',
    logActivity: 'Log Activity',
    allCaughtUp: 'All Caught Up!',
    allCaughtUpDesc: 'You have no new notifications. Check back later for updates and wellness reminders.',
    noResultsFound: 'No Results Found',
    noResultsFoundDesc: "We couldn't find any results for \"{query}\". Try adjusting your search or explore other options.",
  },

  // Footer
  footer: {
    tagline: 'Your AI-Powered Preventive Health Companion',
    builtWith: 'Built with care for your wellness journey',
    allRightsReserved: 'All rights reserved.',
    about: 'About',
    privacy: 'Privacy',
    terms: 'Terms',
    contact: 'Contact',
    helpCenter: 'Help Center',
  },

  // Modals
  modals: {
    feedback: {
      title: 'Share Your Feedback',
      subtitle: 'Help us improve HealthLoop',
      whatWorking: "What's working well?",
      whatImprove: 'What could we improve?',
      placeholder: 'Share your thoughts...',
      thankYou: 'Thank you for your feedback!',
      submitted: 'Your feedback helps us make HealthLoop better.',
    },
    reportProblem: {
      title: 'Report a Problem',
      subtitle: 'Help us fix bugs and improve the app',
      describe: 'Describe the issue',
      placeholder: 'What happened? Please be as specific as possible...',
      optional: 'Optional: Steps to reproduce',
      stepsPlaceholder: '1. I clicked on...\n2. Then I...',
      thankYou: 'Thank you for reporting!',
      submitted: "We'll look into this right away.",
    },
    mealPreferences: {
      title: 'Customize Your Meals',
      subtitle: 'Tell us about your food preferences',
      cuisines: 'Preferred Cuisines',
      selectCuisines: 'Select all that apply',
      dietary: 'Dietary Preferences',
      selectDietary: 'Select your dietary needs',
      allergies: 'Allergies & Restrictions',
      allergiesPlaceholder: 'e.g., Nuts, Shellfish, Gluten',
      save: 'Save Preferences',
      saving: 'Saving...',
    },
    upgrade: {
      title: 'Upgrade to Premium',
      subtitle: 'Unlock all features',
      features: 'Premium Features',
      getStarted: 'Get Started',
      learnMore: 'Learn More',
    },
  },

  // Login & Auth
  login: {
    title: 'Your Preventive Health Intelligence Platform',
    subtitle: 'Track, learn, and take action before problems escalate. Built for prevention, education, engagement, and wellness.',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    forgotPassword: 'Forgot Password?',
    noAccount: "Don't have an account?",
    createAccount: 'Create one',
    biometricLogin: 'Sign in with Biometrics',
    biometricHelper: 'Use fingerprint, Face ID, or Windows Hello',
    authenticating: 'Authenticating...',
    vendorPartner: 'Are you a vendor partner?',
    vendorLogin: 'Vendor Login',
    emailRequired: 'Please enter your email first',
    biometricSuccess: 'Successfully signed in with biometrics! 🎉',
    noPasskeys: 'No biometric login set up. Please use password login first.',
    biometricCancelled: 'Biometric login cancelled',
    biometricFailed: 'Biometric login failed. Please try password login.',
  },

  register: {
    title: 'Join HealthLoop',
    subtitle: 'Start your preventive health journey today',
    fullName: 'Full Name',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    createAccount: 'Create Account',
    haveAccount: 'Already have an account?',
    signIn: 'Sign In',
    creating: 'Creating account...',
    passwordTooShort: 'Password must be at least 8 characters',
    setupBiometric: 'Set Up Biometric Login',
    setupBiometricDesc: 'Use your fingerprint, Face ID, or Windows Hello for faster, more secure login',
    setupNow: 'Set Up Biometric Login',
    skipForNow: 'Skip for Now',
    settingUp: 'Setting up...',
    benefit1: 'No more passwords to remember',
    benefit2: 'Extra secure with biometric verification',
    benefit3: 'Sign in with just a tap or glance',
    biometricPrivacy: 'Your biometric data never leaves your device',
    biometricSuccess: 'Biometric login set up successfully! 🎉',
    biometricFailed: 'Failed to set up biometric login. You can set it up later in settings.',
    biometricSetupLater: 'You can set up biometric login later in your profile settings',
  },

  // Women's Health
  womensHealthPage: {
    title: "Women's Health Tracker",
    subtitle: 'Track your cycle, symptoms, and wellness',
    cycleOverview: 'Cycle Overview',
    currentPhase: 'Current Phase',
    nextPeriod: 'Next Period Expected',
    logPeriod: 'Log Period',
    logSymptoms: 'Log Symptoms',
    insights: 'Cycle Insights',
    tips: 'Phase-Based Tips',
    history: 'Period History',
    noData: 'No cycle data yet',
    noDataDesc: 'Start tracking your cycle to receive personalized insights and predictions.',
    startTracking: 'Start Tracking',
  },

  // Notifications
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark All as Read',
    noNotifications: 'No new notifications',
    viewAll: 'View All',
  },

  // Family & Child Health
  familyHealth: {
    title: 'Family & Child Health',
    subtitle: 'Preventive health support for your entire family',
    tabImmunizations: 'Immunizations',
    tabEducation: 'Education',
    tabReminders: 'Reminders',
    tabPlanner: 'Family Planner',
    
    // Immunizations
    immunizations: {
      title: 'Immunization Awareness',
      subtitle: 'Stay informed about recommended vaccines',
      recommendedSchedule: 'Recommended Schedule',
      byAge: 'By Age Group',
      infants: 'Infants (0-12 months)',
      toddlers: 'Toddlers (1-3 years)',
      preschool: 'Preschool (4-6 years)',
      schoolAge: 'School Age (7-12 years)',
      teens: 'Teens (13-18 years)',
      adults: 'Adults',
      learnMore: 'Learn More',
      consultDoctor: 'Always consult your healthcare provider for personalized vaccine recommendations',
      upcomingReminders: 'Upcoming Vaccine Reminders',
      noReminders: 'No upcoming vaccine reminders',
      addReminder: 'Add Vaccine Reminder',
      reminderFor: 'Reminder for',
      dueDate: 'Due Date',
      notes: 'Notes',
      markComplete: 'Mark Complete',
    },
    
    // Education
    education: {
      title: 'Family Health Education',
      subtitle: 'Evidence-based preventive health information',
      categories: 'Categories',
      infant: 'Infant Care',
      child: 'Child Development',
      nutrition: 'Nutrition & Growth',
      safety: 'Safety & Prevention',
      mental: 'Mental Wellness',
      hygiene: 'Hygiene & Habits',
      searchPlaceholder: 'Search health topics...',
      popularTopics: 'Popular Topics',
      readMore: 'Read More',
      readTime: 'min read',
    },
    
    // Reminders
    reminders: {
      title: 'Family Health Reminders',
      subtitle: 'Stay on track with preventive care for the whole family',
      createReminder: 'Create Reminder',
      familyMember: 'Family Member',
      reminderType: 'Reminder Type',
      frequency: 'Frequency',
      activeReminders: 'Active Reminders',
      upcomingThisWeek: 'Upcoming This Week',
      noActiveReminders: 'No active reminders',
      types: {
        checkup: 'Health Checkup',
        dental: 'Dental Visit',
        vision: 'Vision Screening',
        vaccine: 'Vaccination',
        medication: 'Medication',
        custom: 'Custom',
      },
    },
    
    // Family Planner
    planner: {
      title: 'Family Health Planner',
      subtitle: 'Coordinate preventive care for your family',
      addMember: 'Add Family Member',
      memberName: 'Member Name',
      relationship: 'Relationship',
      dateOfBirth: 'Date of Birth',
      viewCalendar: 'View Calendar',
      upcomingCare: 'Upcoming Preventive Care',
      nothingScheduled: 'Nothing scheduled yet',
      scheduleCheckup: 'Schedule Checkup',
      relationships: {
        child: 'Child',
        spouse: 'Spouse',
        parent: 'Parent',
        sibling: 'Sibling',
        other: 'Other',
      },
    },
    
    // Educational Content
    content: {
      infantCare: {
        title: 'Infant Preventive Care',
        intro: 'Essential preventive health practices for your baby\'s first year',
        topics: [
          'Well-baby checkup schedule',
          'Developmental milestones to watch',
          'Safe sleep practices',
          'Nutrition and feeding',
          'Immunization schedule',
        ],
      },
      childDevelopment: {
        title: 'Child Development & Milestones',
        intro: 'Understanding healthy growth and development',
        topics: [
          'Physical development milestones',
          'Social and emotional growth',
          'Language development',
          'Cognitive milestones',
          'When to talk to your doctor',
        ],
      },
      nutrition: {
        title: 'Nutrition & Healthy Growth',
        intro: 'Building healthy eating habits from the start',
        topics: [
          'Age-appropriate nutrition',
          'Healthy snack ideas',
          'Managing picky eaters',
          'Food safety basics',
          'Hydration guidelines',
        ],
      },
      safety: {
        title: 'Safety & Injury Prevention',
        intro: 'Creating a safe environment for your family',
        topics: [
          'Home safety checklist',
          'Car seat safety',
          'Water safety',
          'Poison prevention',
          'Emergency preparedness',
        ],
      },
    },
  },
};
